/* eslint-disable react/jsx-no-undef */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useEffect, useRef } from "react";
import type { RefObject } from "react";
import { useNavigate } from "react-router-dom";
import {
  Brain, Heart, Target, Clock, Shield, BarChart3,
  CheckCircle, Award, TrendingUp, Users, BookOpen,
  GraduationCap, Briefcase, Smartphone, Globe, Sparkles,
  ArrowRight, Star, ChevronRight, PlayCircle, Video, X, Menu,
  HandHelping, MessageSquare, FileText, School, Home, RefreshCw,
  Zap
} from "lucide-react";
import './Landing.css';

// Image imports - replace with your actual assets
import HeroImage from '../assets/hero-image1.png';
import TeacherImage from '../assets/teacher-image.png';
import ClassroomImage from '../assets/classroom-image.png';
import ParentImage from '../assets/parent-image.png';
import SchoolImage from '../assets/school-image.jpg';
import TeachIDDLogo from '../assets/bb-edtech-logo.png';
import FeatureDemoThumbnail from '../assets/feature-demo-thumbnail.png';
import ProductTourThumbnail from '../assets/product-tour-thumbnail.png';

// YouTube Video IDs (replace with your actual YouTube video IDs for TeachIDD)
const YOUTUBE_VIDEOS = {
  PRODUCT_TOUR: "7ZeI00-4svE",      // Replace with TeachIDD product tour
  IDD_GUIDE_DEMO: "-tCoFMvRCEg",    // Replace with IDD Guide demo
  TEACHER_TRAINING: "kmQMZMfUUrE",  // Replace with teacher training demo
  CLASSROOM_STRATEGIES: "DSBK-oQSN9c", // Replace with classroom strategies
  PARENT_GUIDE: "ZVibVSk60Po"       // Replace with parent guide demo
};

// Generate YouTube embed URLs
const getYouTubeEmbedUrl = (videoId: string, autoplay = false) => {
  return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&showinfo=0&autoplay=${autoplay ? 1 : 0}`;
};

const getYouTubeThumbnailUrl = (videoId: string, quality = "hqdefault") => {
  return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
};

// Core Features for TeachIDD
const coreFeatures = [
  {
    icon: <Brain />,
    title: "IDD Guide AI",
    description: "Personalized teaching strategies for students with Intellectual and Developmental Disabilities. Get step-by-step guidance tailored to each student's unique profile.",
    color: "var(--primary-blue)",
    youtubeId: YOUTUBE_VIDEOS.IDD_GUIDE_DEMO,
    benefits: [
      "Create individualized education plans in minutes",
      "Adapt lessons to student's reading and math level",
      "Get behavior support strategies that work"
    ]
  },
  {
    icon: <MessageSquare />,
    title: "Confusion Solver",
    description: "Break down complex concepts into simple, step-by-step explanations. Perfect for slow learners who need extra support understanding new topics.",
    color: "var(--primary-purple)",
    youtubeId: YOUTUBE_VIDEOS.TEACHER_TRAINING,
    benefits: [
      "Step-by-step explanations with Nigerian examples",
      "Practice problems with immediate feedback",
      "Text-to-speech for low-reading learners"
    ]
  },
  {
    icon: <Users />,
    title: "Teacher Training Mode",
    description: "Real-time support for untrained teachers. Type your classroom challenge, get actionable strategies you can use immediately.",
    color: "var(--primary-green)",
    youtubeId: YOUTUBE_VIDEOS.CLASSROOM_STRATEGIES,
    benefits: [
      "No special ed degree required",
      "Exact phrases to say to students",
      "5-minute strategies you can use today"
    ]
  },
  {
    icon: <Home />,
    title: "Parent & Caregiver Mode",
    description: "Continuity of learning at home. Parents get the same AI guidance to support their child's learning outside school hours.",
    color: "var(--primary-orange)",
    youtubeId: YOUTUBE_VIDEOS.PARENT_GUIDE,
    benefits: [
      "Offline-friendly activities",
      "Simple instructions for non-experts",
      "Track progress across school and home"
    ]
  }
];

// Who It's For - Target audiences for TeachIDD
const targetAudiences = [
  {
    title: "Mainstream Teachers",
    icon: <Users />,
    image: TeacherImage,
    needs: [
      "No special ed training? No problem",
      "Teach IDD and slow learners alongside other students",
      "Get real-time strategies during class"
    ]
  },
  {
    title: "Special Educators",
    icon: <GraduationCap />,
    image: ClassroomImage,
    needs: [
      "Save time on IEP creation",
      "Access evidence-based strategies",
      "Scale your impact across more students"
    ]
  },
  {
    title: "Parents & Caregivers",
    icon: <Heart />,
    image: ParentImage,
    needs: [
      "Support learning at home with confidence",
      "No special equipment needed",
      "Simple activities that work"
    ]
  },
  {
    title: "Schools & Institutions",
    icon: <School />,
    image: SchoolImage,
    needs: [
      "Affordable teacher training at scale",
      "Track teacher progress and usage",
      "Comply with inclusive education policies"
    ]
  }
];

// Benefits specific to TeachIDD
const benefits = [
  { icon: <Zap />, stat: "82%", desc: "Teachers lack training — we fill the gap" },
  { icon: <TrendingUp />, stat: "3.75M", desc: "New learners you can reach" },
  { icon: <Target />, stat: "91%", desc: "Content lacks accessibility — we fix that" },
  { icon: <Shield />, stat: "24/7", desc: "On-demand teacher support" }
];

// How It Works Steps
const workflowSteps = [
  { number: "01", title: "Create Student Profile", desc: "Fill a simple 2-minute form about the student's strengths, challenges, and interests. No clinical diagnosis required." },
  { number: "02", title: "Ask Your Question", desc: "Type your classroom challenge: 'How do I teach fractions to a slow learner?' or 'What activity works for a non-verbal student?'" },
  { number: "03", title: "Get Actionable Strategies", desc: "AI gives you 3 specific strategies, exact phrases to say, and a simple activity to try today." },
  { number: "04", title: "Apply & See Results", desc: "Use the strategy immediately. Come back to share what worked and get next steps." }
];

// Testimonials
const testimonials = [
  {
    quote: "I've been teaching for 10 years with no special ed training. IDD Guide gave me a strategy for my autistic student that worked in 5 minutes. I almost cried.",
    author: "Grace Okafor",
    role: "Primary School Teacher, Lagos",
    score: "First inclusive teaching success"
  },
  {
    quote: "My son was always left behind in class. With the parent guide, I can now support him at home. The AI explains things in a way even I understand.",
    author: "Mr. Adebayo",
    role: "Parent of IDD learner",
    score: "Learning at home improved"
  },
  {
    quote: "As a new special ed teacher, I was overwhelmed. IDD Guide became my co-teacher. It saves me hours of planning and gives me confidence.",
    author: "Chidinma Eze",
    role: "Special Education Teacher, Abuja",
    score: "Planning time reduced by 60%"
  },
  {
    quote: "We deployed IDD Guide to 20 teachers with zero training. Within a week, they were using strategies confidently. This is the tool Nigeria needs.",
    author: "Dr. Bola Adeyemi",
    role: "School Administrator, Ibadan",
    score: "Teacher confidence up 70%"
  }
];

// Custom Hook for Scroll Animation
const useScrollAnimation = (threshold = 0.2) => {
  const ref = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold }
    );

    if (currentRef) observer.observe(currentRef);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [threshold]);

  return [ref, isVisible] as [RefObject<HTMLElement>, boolean];
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentVideo, setCurrentVideo] = useState<string | null>(null);
  const [videoTitle, setVideoTitle] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);

  // Navigation handlers
  const handleGetStarted = () => navigate('/signup');
  const handleLogin = () => navigate('/login');
  const handleTryDemo = () => navigate('/demo');
  const handleLearnMore = () => navigate('/features');

  // Header scroll effect
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial(prev => (prev + 1) % testimonials.length);
    }, 7000);
    return () => clearInterval(interval);
  }, []);

  // Auto-rotate features
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFeatureIndex(prev => (prev + 1) % coreFeatures.length);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  // Handle video playback
  const playVideo = (youtubeId: string, title: string) => {
    setCurrentVideo(youtubeId);
    setVideoTitle(title);
    setShowVideoModal(true);
    document.body.style.overflow = 'hidden';
  };

  const closeVideoModal = () => {
    setShowVideoModal(false);
    setCurrentVideo(null);
    document.body.style.overflow = 'auto';
  };

  const handleFeatureDemo = (featureIndex: number) => {
    const feature = coreFeatures[featureIndex];
    if (feature.youtubeId) {
      playVideo(feature.youtubeId, `${feature.title} Demo`);
    }
  };

  const handleProductTour = () => {
    playVideo(YOUTUBE_VIDEOS.PRODUCT_TOUR, "TeachIDD Product Tour");
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeVideoModal();
    };
    
    if (showVideoModal) {
      window.addEventListener('keydown', handleEscape);
    }
    
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [showVideoModal]);

  const handleMobileNavClick = (sectionId: string) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  // Section Refs
  const [heroRef, heroVisible] = useScrollAnimation(0.3);
  const [featuresRef, featuresVisible] = useScrollAnimation(0.3);
  const [audienceRef, audienceVisible] = useScrollAnimation(0.3);
  const [workflowRef, workflowVisible] = useScrollAnimation(0.3);
  const [testimonialsRef, testimonialsVisible] = useScrollAnimation(0.3);

  return (
    <div className="landing-page">
      {/* Video Modal */}
      {showVideoModal && currentVideo && (
        <div className="video-modal-overlay" onClick={closeVideoModal}>
          <div className="video-modal" onClick={(e) => e.stopPropagation()}>
            <div className="video-modal-header">
              <h3>{videoTitle}</h3>
              <button className="close-modal-btn" onClick={closeVideoModal}>
                <X size={24} />
              </button>
            </div>
            <div className="video-container">
              <iframe
                src={getYouTubeEmbedUrl(currentVideo, true)}
                title={videoTitle}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="youtube-iframe"
              />
            </div>
            <div className="video-modal-footer">
              <p>Watch more videos on our <a href={`https://www.youtube.com/watch?v=${currentVideo}`} target="_blank" rel="noopener noreferrer">YouTube channel</a></p>
            </div>
          </div>
        </div>
      )}

      {/* Header/Navigation */}
      <header className={`header ${scrolled ? 'scrolled' : ''}`}>
        <div className="header-container">
          <div className="logo-section">
            <div className="logo-placeholder">
              <img 
                src={TeachIDDLogo} 
                alt="TeachIDD Logo" 
                className="logo-image"
              />
              <div className="logo-text">
                <span className="logo-main">TeachIDD</span>
                <span className="logo-sub"><sup>AI</sup></span>
              </div>
            </div>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="nav-menu desktop-nav">
            <button 
              className="nav-link" 
              onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Features
            </button>
            <button 
              className="nav-link" 
              onClick={() => document.getElementById('who-its-for')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Who It's For
            </button>
            <button 
              className="nav-link" 
              onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
            >
              How It Works
            </button>
            <button 
              className="nav-link" 
              onClick={() => document.getElementById('testimonials')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Success Stories
            </button>
          </nav>
          
          <div className="header-actions">
            <button className="login-btn" onClick={handleLogin}>
              Sign In
            </button>
            
            <button 
              className="mobile-menu-btn" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <Menu size={24} />
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="mobile-nav-menu">
            <button 
              className="mobile-nav-link" 
              onClick={() => handleMobileNavClick('features')}
            >
              Features
            </button>
            <button 
              className="mobile-nav-link" 
              onClick={() => handleMobileNavClick('who-its-for')}
            >
              Who It's For
            </button>
            <button 
              className="mobile-nav-link" 
              onClick={() => handleMobileNavClick('how-it-works')}
            >
              How It Works
            </button>
            <button 
              className="mobile-nav-link" 
              onClick={() => handleMobileNavClick('testimonials')}
            >
              Success Stories
            </button>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section
        ref={heroRef}
        className={`hero-section ${heroVisible ? "fade-in" : "fade-hidden"}`}
      >
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">
              <Sparkles size={16} />
              <span>AI-Powered Inclusive Teaching Assistant</span>
            </div>
            
            <h1 className="hero-title">
              Every Teacher Can Teach
              <span className="gradient-text"> Every Learner</span>
            </h1>
            
            <p className="hero-description"> 
              TeachIDD gives untrained teachers and parents the tools to support 
              students with intellectual disabilities and slow learners — no special 
              education degree required.
            </p>
            
            <div className="hero-stats">
              {benefits.map((benefit, index) => (
                <div key={index} className="stat-item">
                  <div className="stat-icon">{benefit.icon}</div>
                  <div className="stat-value">{benefit.stat}</div>
                  <div className="stat-label">{benefit.desc}</div>
                </div>
              ))}
            </div>
            
            <div className="hero-actions">
              <button className="cta-primary" onClick={handleGetStarted}>
                Start Teaching Inclusively
              </button>
              <button className="cta-secondary" onClick={handleProductTour}>
                <Video size={20} />
                Watch How It Works
              </button>
            </div>
            
            <div className="hero-trust">
              <div className="trust-item">
                <Shield size={20} />
                <span>No Special Ed Degree Needed</span>
              </div>
              <div className="trust-item">
                <Target size={20} />
                <span>Strategies That Work Today</span>
              </div>
              <div className="trust-item">
                <RefreshCw size={20} />
                <span>Works Offline & On Any Phone</span>
              </div>
            </div>
          </div>
          
          <div className="hero-visual">
            <div className="hero-image-container">
              <img 
                src={HeroImage} 
                alt="Teacher supporting student with AI assistance" 
                className="hero-main-image"
              />
              <div className="hero-image-overlay">
                <div className="floating-card teacher-card">
                  <Users size={24} />
                  <span>82% of teachers lack training</span>
                </div>
                <div className="floating-card solution-card">
                  <Brain size={24} />
                  <span>TeachIDD fills the gap</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Features with Auto-rotation */}
      <section
        id="features"
        ref={featuresRef}
        className={`features-section ${featuresVisible ? "fade-in" : "fade-hidden"}`}
      >
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">How TeachIDD Supports You</h2>
            <p className="section-subtitle">
              Four AI tools, one mission: inclusive education for all
            </p>
          </div>
          
          {/* Auto-rotating Feature Display */}
          <div className="feature-detail">
            <div className="feature-content">
              <div className="feature-icon-large" style={{ color: coreFeatures[currentFeatureIndex].color }}>
                {coreFeatures[currentFeatureIndex].icon}
              </div>
              <h3 className="feature-detail-title">{coreFeatures[currentFeatureIndex].title}</h3>
              <p className="feature-detail-desc">{coreFeatures[currentFeatureIndex].description}</p>
              <ul className="feature-benefits">
                {coreFeatures[currentFeatureIndex].benefits.map((benefit, idx) => (
                  <li key={idx}><CheckCircle size={18} /> {benefit}</li>
                ))}
              </ul>
              <button 
                className="learn-more-btn"
                onClick={() => handleFeatureDemo(currentFeatureIndex)}
              >
                <PlayCircle size={16} />
                Watch Feature Demo <ArrowRight size={16} />
              </button>
            </div>
            <div className="feature-visual">
              <div className="feature-demo-container">
                <div className="demo-video-placeholder">
                  <div 
                    className="demo-play-button"
                    onClick={() => handleFeatureDemo(currentFeatureIndex)}
                  >
                    <div className="play-icon-wrapper">
                      <PlayCircle size={48} />
                    </div>
                    <div className="demo-text">
                      <h4>Watch {coreFeatures[currentFeatureIndex].title} Demo</h4>
                      <p>See how this tool works in real classrooms</p>
                    </div>
                  </div>
                  <div className="demo-thumbnail">
                    {coreFeatures[currentFeatureIndex].youtubeId ? (
                      <img 
                        src={getYouTubeThumbnailUrl(coreFeatures[currentFeatureIndex].youtubeId, "maxresdefault")}
                        alt={`${coreFeatures[currentFeatureIndex].title} Demo`}
                        onError={(e) => {
                          e.currentTarget.src = FeatureDemoThumbnail;
                        }}
                      />
                    ) : (
                      <img 
                        src={FeatureDemoThumbnail} 
                        alt={`${coreFeatures[currentFeatureIndex].title} Demo`} 
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Feature Indicators */}
          <div className="feature-indicators">
            {coreFeatures.map((_, index) => (
              <button
                key={index}
                className={`feature-dot ${index === currentFeatureIndex ? 'active' : ''}`}
                onClick={() => setCurrentFeatureIndex(index)}
                aria-label={`View feature ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section
        id="who-its-for"
        ref={audienceRef}
        className={`audience-section ${audienceVisible ? "fade-in" : "fade-hidden"}`}
      >
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Who Uses TeachIDD<sup>AI</sup></h2>
            <p className="section-subtitle">
              Built for everyone who supports learners with disabilities
            </p>
          </div>
          
          <div className="audience-grid">
            {targetAudiences.map((audience, index) => (
              <div key={index} className="audience-card">
                <div className="audience-image">
                  <img 
                    src={audience.image} 
                    alt={audience.title} 
                    className="audience-img"
                  />
                  <div className="audience-icon-overlay">
                    {audience.icon}
                  </div>
                </div>
                <div className="audience-content">
                  <h3 className="audience-title">{audience.title}</h3>
                  <ul className="audience-needs">
                    {audience.needs.map((need, idx) => (
                      <li key={idx} className="need-item">
                        <Star size={16} />
                        <span>{need}</span>
                      </li>
                    ))}
                  </ul>
                  <button className="audience-cta" onClick={handleGetStarted}>
                    Start Free Trial
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section
        id="how-it-works"
        ref={workflowRef}
        className={`workflow-section ${workflowVisible ? "fade-in" : "fade-hidden"}`}
      >
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">From Challenge to Solution in 4 Steps</h2>
            <p className="section-subtitle">
              No training required. Just real help when you need it.
            </p>
          </div>
          
          <div className="workflow-steps">
            {workflowSteps.map((step, index) => (
              <div key={index} className="step-card">
                <div className="step-number">{step.number}</div>
                <div className="step-content">
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-desc">{step.desc}</p>
                </div>
                {index < workflowSteps.length - 1 && (
                  <div className="step-connector">
                    <ArrowRight size={24} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Tour Section */}
      <section className="product-tour-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">See TeachIDD in Action</h2>
            <p className="section-subtitle">
              Watch how our AI helps teachers and parents support every learner
            </p>
          </div>
          
          <div className="tour-container">
            <div className="tour-video-container">
              <div className="tour-video-wrapper">
                <div 
                  className="tour-play-overlay" 
                  onClick={handleProductTour}
                >
                  <div className="play-button-large">
                    <PlayCircle size={64} />
                  </div>
                  <div className="tour-play-text">
                    <h3>Watch Full Product Tour</h3>
                    <p>3-minute overview of all features</p>
                  </div>
                </div>
                <div className="video-thumbnail">
                  {YOUTUBE_VIDEOS.PRODUCT_TOUR ? (
                    <img 
                      src={getYouTubeThumbnailUrl(YOUTUBE_VIDEOS.PRODUCT_TOUR, "maxresdefault")}
                      alt="Product Tour Thumbnail"
                      className="tour-thumbnail-img"
                      onError={(e) => {
                        e.currentTarget.src = ProductTourThumbnail;
                      }}
                    />
                  ) : (
                    <img 
                      src={ProductTourThumbnail} 
                      alt="Product Tour Thumbnail" 
                      className="tour-thumbnail-img"
                    />
                  )}
                </div>
              </div>
            </div>
            
            <div className="tour-highlights">
              <h3>What You'll See in the Tour:</h3>
              <ul className="highlight-list">
                <li><CheckCircle size={18} /> Creating a student profile in 2 minutes</li>
                <li><CheckCircle size={18} /> Asking your first teaching question</li>
                <li><CheckCircle size={18} /> Getting actionable classroom strategies</li>
                <li><CheckCircle size={18} /> Using parent mode for home learning</li>
                <li><CheckCircle size={18} /> Working offline on any phone</li>
                <li><CheckCircle size={18} /> Real teacher testimonials</li>
              </ul>
              <button className="tour-cta-btn" onClick={handleProductTour}>
                <Video size={20} />
                Start Watching Now
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem We Solve - Mastercard Alignment */}
      <section className="problem-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">The Problem We're Solving</h2>
            <p className="section-subtitle">
              According to Mastercard's research with Nigerian educators
            </p>
          </div>
          
          <div className="problem-grid">
            <div className="problem-card">
              <div className="problem-stat">82%</div>
              <h4>of teachers cannot effectively teach students with disabilities</h4>
              <p>TeachIDD gives every teacher an AI co-teacher — no special training required.</p>
            </div>
            <div className="problem-card">
              <div className="problem-stat">91%</div>
              <h4>of digital learning content lacks basic accessibility</h4>
              <p>TeachIDD is built for accessibility first — screen reader compatible, text-to-speech, and teacher-as-interface mode.</p>
            </div>
            <div className="problem-card">
              <div className="problem-stat">3.75M</div>
              <h4>learners could be reached through inclusive design</h4>
              <p>TeachIDD expands Nigeria's EdTech market by making inclusive education possible at scale.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section
        id="testimonials"
        ref={testimonialsRef}
        className={`testimonials-section ${testimonialsVisible ? "fade-in" : "fade-hidden"}`}
      >
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Trusted by Nigerian Educators and Parents</h2>
            <p className="section-subtitle">
              Real stories from real users
            </p>
          </div>
          
          <div className="testimonials-container">
            <div className="testimonials-slider">
              {testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className={`testimonial-card ${index === activeTestimonial ? 'active' : ''}`}
                >
                  <div className="quote-mark">"</div>
                  <p className="testimonial-text">{testimonial.quote}</p>
                  <div className="testimonial-author">
                    <div className="author-info">
                      <strong>{testimonial.author}</strong>
                      <span>{testimonial.role}</span>
                    </div>
                    <div className="testimonial-score">
                      <Award size={20} />
                      <span>{testimonial.score}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="testimonial-dots">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  className={`dot ${index === activeTestimonial ? 'active' : ''}`}
                  onClick={() => setActiveTestimonial(index)}
                  aria-label={`View testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Teach Every Learner?</h2>
            <p className="cta-subtitle">
              Join hundreds of Nigerian teachers and parents who are using TeachIDD to create inclusive classrooms and homes.
            </p>
            <div className="cta-buttons">
              <button className="cta-btn-primary" onClick={handleGetStarted}>
                Start Your Free Trial
              </button>
              <button className="cta-btn-secondary" onClick={handleProductTour}>
                <Video size={20} />
                Watch Product Tour
              </button>
            </div>
            <div className="cta-features">
              <div className="feature-item">
                <CheckCircle size={20} />
                <span>No credit card required</span>
              </div>
              <div className="feature-item">
                <CheckCircle size={20} />
                <span>Works on any phone</span>
              </div>
              <div className="feature-item">
                <CheckCircle size={20} />
                <span>Offline mode included</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="footer-logo">
                <img 
                  src={TeachIDDLogo} 
                  alt="TeachIDD Logo" 
                  className="footer-logo-image"
                />
                <div className="footer-logo-text">
                  <span className="logo-main">TeachIDD</span>
                  <span className="logo-sub"><sup>AI</sup></span>
                </div>
              </div>
              <p className="footer-tagline">
                AI-powered inclusive teaching assistant for Nigerian educators and parents.
              </p>
            </div>
            
            <div className="footer-links">
              <div className="footer-column">
                <h4>Product</h4>
                <ul>
                  <li><button onClick={handleLearnMore}>Features</button></li>
                  <li><button onClick={() => navigate('/pricing')}>Pricing</button></li>
                  <li><button onClick={handleProductTour}>Product Tour</button></li>
                  <li><button onClick={() => navigate('/demo')}>Request Demo</button></li>
                </ul>
              </div>
              
              <div className="footer-column">
                <h4>Resources</h4>
                <ul>
                  <li><button onClick={() => navigate('/blog')}>Teaching Blog</button></li>
                  <li><button onClick={() => navigate('/guides')}>Inclusive Ed Guides</button></li>
                  <li><button onClick={() => navigate('/webinars')}>Teacher Webinars</button></li>
                  <li><button onClick={() => navigate('/support')}>Help Center</button></li>
                </ul>
              </div>
              
              <div className="footer-column">
                <h4>Company</h4>
                <ul>
                  <li><button onClick={() => navigate('/about')}>About Us</button></li>
                  <li><button onClick={() => navigate('/contact')}>Contact</button></li>
                  <li><button onClick={() => navigate('/partners')}>Partnerships</button></li>
                  <li><button onClick={() => navigate('/privacy')}>Privacy</button></li>
                </ul>
              </div>
              
              <div className="footer-column">
                <h4>For Schools</h4>
                <ul>
                  <li><button onClick={() => navigate('/schools')}>School Plan</button></li>
                  <li><button onClick={() => navigate('/districts')}>District Solutions</button></li>
                  <li><button onClick={() => navigate('/training')}>Teacher Training</button></li>
                  <li><button onClick={() => navigate('/case-studies')}>Case Studies</button></li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>© {new Date().getFullYear()} TeachIDD<sup>AI</sup> by BB AITECH Solutions. All rights reserved.</p>
            <div className="legal-links">
              <button onClick={() => navigate('/terms')}>Terms</button>
              <button onClick={() => navigate('/privacy')}>Privacy</button>
              <button onClick={() => navigate('/cookies')}>Cookies</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}